

// ==================== Vehicle & Batch Types ====================

export interface VehicleSpec {
  series: string;
  model: string;
  trim: string;
  color: string;
  powertrain: string;
  model_year: number;
}

export interface DeviationBatch {
  id: string;
  vehicle_spec: VehicleSpec;
  quantity: number;
  deviation_type: 'surplus' | 'shortage' | 'swap';
  origin_plant: string;
  created_at?: string;
}

export interface StrategyOption {
  impact_metric_states?: Array<{
    key: string;
    status: 'computed' | 'no_material_change' | 'unavailable';
    value?: number;
    reason?: string;
  }>;
  id: string;
  title: string;
  description: string;
  reasoning: string;
  impact_estimates: {
    delta_transport_cost?: number;
    delta_holding_cost?: number;
    delta_sell_through?: number;
    // Shortage / Mixed fields
    delta_fulfillment?: number;
    delta_wait_time?: number;
    equity_score?: number;
    // Reconciliation fields
    delta_build_efficiency?: number;
    policy_compliance?: number;
    delta_units_rebalanced?: number;
    [key: string]: number | undefined; // Allow flexibility
  };
  cited_shocks: string[];
  is_recommended?: boolean;
}

// ==================== Region Types ====================

export type Region =
  | 'PORTLAND'
  | 'SAN_FRANCISCO'
  | 'LOS_ANGELES'
  | 'DENVER'
  | 'CHICAGO'
  | 'KANSAS_CITY'
  | 'GULF'
  | 'SOUTHEAST_TOYOTA'
  | 'CENTRAL_ATLANTIC'
  | 'CINCINNATI'
  | 'NEW_YORK'
  | 'BOSTON';

export const REGION_DISPLAY_NAMES: Record<string, string> = {
  PORTLAND: 'Portland',
  SAN_FRANCISCO: 'San Francisco',
  LOS_ANGELES: 'Los Angeles',
  DENVER: 'Denver',
  CHICAGO: 'Chicago',
  KANSAS_CITY: 'Kansas City',
  GULF: 'Gulf',
  SOUTHEAST_TOYOTA: 'Southeast Toyota',
  CENTRAL_ATLANTIC: 'Central Atlantic',
  CINCINNATI: 'Cincinnati',
  NEW_YORK: 'New York',
  BOSTON: 'Boston',
};

export const getRegionDisplayName = (region: string): string => {
  const normalized = region.toUpperCase().replace(/ /g, '_');
  return REGION_DISPLAY_NAMES[normalized] || region;
};

// ==================== Allocation Types ====================

export interface RegionAllocation {
  region: Region;
  allocated_units: number;
  allocation_percent: number;
  transport_cost: number;
  projected_holding_cost: number;
  incentive_cost: number;
  total_cost: number;
  projected_days_to_sell: number;
  velocity_score: number;
  reasoning: string;
}

export interface RegionDeficitAllocation {
  region: Region;
  demand: number;
  allocated: number;
  shortfall: number;
  fulfillment_rate: number;
  base_units: number;
  proportional_units: number;
  merit_adjustment: number;
  merit_score: number;
  rank: number;
  reasoning: string;
}

export interface ExclusionReason {
  category: string;
  severity: 'blocking' | 'major' | 'minor';
  description: string;
  actual_value?: number;
  threshold_value?: number;
}

export interface ExcludedRegion {
  region: Region;
  primary_reason: string;
  reasons: ExclusionReason[];
  velocity_score: number;
  available_capacity: number;
  cost_score: number;
  was_close_call: boolean;
  comparative_rank?: number;
  severity?: 'critical' | 'high' | 'medium' | 'low';
}

export interface AllocationPlan {
  batch_id: string;
  deviation_batch_id?: string;
  batch_allocations?: AllocationPlan[];
  allocations: RegionAllocation[];
  excluded_regions: ExcludedRegion[];
  total_units_allocated: number;
  total_transport_cost: number;
  total_holding_cost: number;
  total_incentive_cost: number;
  total_cost: number;
  weighted_avg_days_to_sell: number;
  executive_summary: string;
  detailed_reasoning: string | string[];
  risk_factors: string[];
}

export interface DeficitAllocationPlan {
  allocations: RegionDeficitAllocation[];
  total_demand: number;
  total_supply: number;
  total_allocated: number;
  shortfall: number;
  fulfillment_rate: number;
  success: boolean;
  message: string;
}

export interface MixedAllocationPlan {
  surplus_plans: AllocationPlan[];
  deficit_plans: DeficitAllocationPlan[];
  batch_ids: Record<string, string>;
}

// ==================== Scenario Types ====================

export interface Scenario {
  id: string;
  name?: string;
  title?: string;
  description?: string;
  scenario_type?: 'single' | 'multi-batch' | 'mixed' | 'reconciliation';
  batches?: DeviationBatch[];
  deviation_batch?: DeviationBatch;
  regional_demands?: Record<string, number>;
  total_demand?: number;
  total_supply?: number;
}

export interface Deviation {
  id: string;
  scenario_id?: string;
  title: string;
  scenario_type: string;
  batches: DeviationBatch[];
  total_demand?: number;
  created_at?: string;
}

// ==================== Result Types ====================

export type ApprovalStatus = 'proposed' | 'approved' | 'rejected';

export interface ComparisonStats {
  transport?: number;
  holding?: number;
  days?: number;
  units?: number;
  fulfillment_pct?: number;
  supply?: number;
  avg_merit?: number;
  shortfall?: number;
  target_gap?: number;
  compliance?: number;
  units_rebalanced?: number;
}

export interface BusinessComparisonMetric {
  key: string;
  label: string;
  format: 'currency' | 'days' | 'percent' | 'number' | 'number_1dp' | string;
  better_when: 'lower' | 'higher' | string;
  current_value: number;
  baseline_value: number;
  delta: number;
  trend: 'improved' | 'regressed' | 'neutral' | string;
}

export type ComparisonState =
  | 'comparable_high_confidence'
  | 'comparable_low_confidence'
  | 'not_comparable'
  | 'insufficient_history'
  | 'comparison_filter_invalid';

export interface ComparisonCandidate {
  candidate_id: string;
  source?: string;
  candidate_type?: 'actualized' | 'approved_only' | string;
  rank?: number;
  label?: string;
  selected_reference_mode?: 'actualized_outcome' | 'approved_plan' | string;
  selected_reference_label?: string;
  approved_run_id?: string | null;
  approved_summary?: string | null;
  actualized_id?: string | null;
  actualized_summary?: string | null;
  historical_case?: {
    title?: string;
    subtitle?: string;
    event_date?: string;
    match_reason?: string;
    summary?: string;
    context_lines?: string[];
  };
  current_vs_selected_metrics?: BusinessComparisonMetric[];
  approved_vs_actualized_metrics?: BusinessComparisonMetric[];
  brief?: {
    kicker?: string;
    summary?: string;
    implication?: string;
    highlights?: string[];
    reference_label?: string;
    case_summary?: string;
    why_this_match?: string;
    current_vs_selected_summary?: string;
    approved_vs_actualized_summary?: string;
  };
  run_quality?: {
    runtime_guardrails?: Record<string, any>;
    token_cost?: Record<string, any>;
    decision_ledger?: Record<string, any>;
    scenario_proof?: Record<string, any>;
  };
}

export interface BusinessComparisonPayload {
  anchor_mode: 'whatif_in_session_baseline' | 'same_scenario_last_approved' | string;
  anchor_label: string;
  anchor_run_id?: string | null;
  scenario_family?: string | null;
  state?: ComparisonState;
  reason_codes?: string[];
  claim_suppressed?: boolean;
  claim_policy?: Record<string, any>;
  filter_context?: {
    requested_filters?: Record<string, string[]>;
    applied_filters?: Record<string, string[]>;
    invalid?: boolean;
    reason_codes?: string[];
  } | null;
  comparability?: {
    score?: number;
    level?: 'none' | 'low' | 'medium' | 'high' | string;
    reasons?: string[];
  };
  reliability_confidence?: {
    level?: string;
    note?: string;
    label_coverage?: {
      labeled_rows?: number | null;
      coverage_ratio?: number | null;
      quality_verdict_coverage?: number | null;
    } | null;
    sample_size?: number | null;
    source?: string;
  };
  attribution_hints?: string[];
  metrics?: BusinessComparisonMetric[];
  headline?: {
    status?: string;
    summary?: string;
  };
  comparison_brief?: {
    kicker?: string;
    summary?: string;
    implication?: string;
    highlights?: string[];
    reference_label?: string;
    case_summary?: string;
    why_this_match?: string;
    current_vs_selected_summary?: string;
    approved_vs_actualized_summary?: string;
  };
  comparison_candidates?: ComparisonCandidate[];
  selected_candidate_id?: string | null;
  replay_evidence?: {
    status?: string;
    reason_codes?: string[];
    confidence?: { level?: string; sample_size?: number };
    window?: { used_history_runs?: number };
  };
  best_available?: {
    source?: 'exact_actualized' | 'close_actualized' | 'approved_anchor' | 'none' | string;
    anchor_run_id?: string | null;
    actualized_id?: string | null;
    match_score?: number | null;
  };
  actualized_evidence?: {
    eligible?: boolean;
    match_type?: 'exact_actualized' | 'close_actualized' | string | null;
    actualized_id?: string | null;
    approved_run_id?: string | null;
    observed_metrics?: Record<string, number>;
    matching_dimensions?: Record<string, string[] | string>;
    outcome_summary?: string | null;
    outcome_notes?: string[];
    match_score?: number | null;
    dimension_score?: number | null;
    metric_score?: number | null;
    reason_codes?: string[];
  };
}

export interface NormalizationNote {
  shown: boolean;
  message: string;
  details?: Record<string, { requested: number; effective: number }>;
}

export interface AllocationResult {
  run_id: string;
  scenario_id: string;
  status: 'success' | 'failed';
  approval_status: ApprovalStatus;
  allocation_plan?: AllocationPlan;
  multi_allocation_plan?: AllocationPlan;
  deficit_allocation_plan?: DeficitAllocationPlan;
  mixed_allocation_plan?: MixedAllocationPlan;
  reconciliation_result?: any;
  reconciliation_scenario?: any;
  executive_summary?: string;
  detailed_reasoning?: string | string[];
  risk_factors?: string[];
  excluded_region_explanations?: Record<string, string>;
  error?: string;
  weights?: Record<string, number> | null;
  user_modifications?: any;
  stored_at?: string;
  plan_type?: 'surplus' | 'multi_batch' | 'deficit' | 'mixed' | 'reconciliation';
  comparison_stats?: ComparisonStats;
  business_comparison?: BusinessComparisonPayload;
  region_contexts?: RegionContextData[];
  selected_strategy?: string;
  normalization_note?: NormalizationNote;
  comparison_filters?: Record<string, any>;
  chat_brief?: string;
  suggested_questions?: string[];
}

export interface LatestAllocation extends AllocationResult {
  scenario_title?: string;
  shortfall_remaining?: number;
  fulfillment_summary?: string;
}

// ==================== Agent Types ====================

export type AgentId = 'orchestrator' | 'strategist' | 'constraint' | 'optimizer' | 'compiler';

export interface Agent {
  id: AgentId;
  label: string;
  icon: string;
  emoji: string;
  color: string;
}

export const AGENTS: Agent[] = [
  { id: 'orchestrator', label: 'Orchestrator', icon: 'fa-sitemap', emoji: '🎯', color: '#e74c3c' },
  { id: 'strategist', label: 'Strategic Director', icon: 'fa-chess-knight', emoji: '🧠', color: '#9b59b6' },
  { id: 'constraint', label: 'Constraint Advisor', icon: 'fa-clipboard-check', emoji: '⚖️', color: '#3498db' },
  { id: 'optimizer', label: 'Distribution Optimizer', icon: 'fa-chart-pie', emoji: '📊', color: '#27ae60' },
  { id: 'compiler', label: 'Insight Compiler', icon: 'fa-file-lines', emoji: '📋', color: '#f39c12' },
];

export type AgentStatus = 'idle' | 'active' | 'completed' | 'monitoring';

// ==================== Trace & Event Types ====================

export interface TraceEvent {
  event_type: 'start' | 'trace' | 'complete' | 'error' | 'progress' | 'skip';
  node: string;
  message: string;
  data?: any;
  timestamp?: string;
}

export interface ActionFeedItem {
  icon: string;
  title: string;
  body: string;
  style: 'info' | 'success' | 'warning' | 'error';
  details?: any;
}

// ==================== Chat Types ====================

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
}

export interface ChatResponse {
  response: string;
  pending_scenario_id?: string;
  should_run?: string;
  whatif_weights?: Record<string, number>;
  global_supply_override?: number;
  global_demand_override?: number;
  parameter_overrides?: Record<string, number>;
  regional_overrides?: Record<string, any>;
  batch_overrides?: Record<string, Record<string, number>>;
  chat_brief?: string;
  suggested_questions?: string[];
}

// ==================== SSE Event Types ====================

export interface SSEEvent {
  type: 'start' | 'trace' | 'complete' | 'error' | 'await_confirmation' | 'resume';
  event?: TraceEvent;
  allocation_plan?: AllocationPlan;
  deficit_allocation_plan?: DeficitAllocationPlan;
  mixed_allocation_plan?: MixedAllocationPlan;
  message?: string;
  run_id?: string;
  checkpoint_data?: CheckpointData;
  confirmation_id?: string;
  [key: string]: any;
}

// ==================== Checkpoint Types ====================

export interface RegionContextData {
  region: string;
  current_units: number;
  capacity: number;
  velocity_score: number;
  units_sold_last_30d: number;
  regional_demand?: number; // Optimizer/order demand used in shortage allocation
  forecasted_demand: number;
  forecast_confidence: number;
  transport_cost: number;
  holding_cost_per_day: number;
  requires_incentive: boolean;
  incentive_amount: number;
  days_to_sell_estimate?: number;
  priority_score?: number;
  strategic_priority?: 'Normal' | 'High' | 'Urgent';
  priority_reasoning?: string;
}

export interface CheckpointData {
  confirmation_id: string;
  strategy_options?: StrategyOption[]; // For Gate 1
  region_contexts?: RegionContextData[]; // For Gate 2
  is_whatif?: boolean;
  normalization_note?: NormalizationNote;
  comparison_filters?: Record<string, any>;
  batches?: {
    id: string;
    deviation_type: string;
    quantity: number;
    vehicle_label?: string;
  }[];
  current_weights?: {
    velocity: number;
    transport: number;
    holding: number;
    capacity: number;
  };
  scenario_id: string;
  plan_type?: string;
  national_build_target?: number;
  policy_toggles?: Record<string, boolean>;
  current_node?: string;
}

// ==================== Distribution/Breakdown Types ====================

export interface DistributionBreakdown {
  region: string;
  base: number;
  proportional: number;
  merit: number;
  total: number;
  fulfillmentPercent: number;
}

// ==================== CVI Agent Types ====================

export interface CVISelectionState {
  brand_division?: string | null;
  year?: string | null;
  series?: string | null;
  region?: string | null;
  feature?: string | null;
  [key: string]: string | null | undefined;
}

export interface CVIToolTrace {
  tool: string;
  args: any;
  output: any;
}

export interface CVIMessage {
  role: 'user' | 'assistant' | 'system' | 'tool';
  content: string;
  tool_calls?: any[];
  tool_call_id?: string;
}

export interface CVIResponse {
  messages: CVIMessage[];
  selection: CVISelectionState;
  tool_trace: CVIToolTrace[];
  done?: boolean;
}

export interface CVIMessage {
  role: 'user' | 'assistant' | 'system' | 'tool';
  content: string;
}

export interface CVIChatResponse {
  messages: CVIMessage[];
  selection: CVISelectionState;
  tool_trace: CVIToolTrace[];
}
